import Hashes from "jshashes";
import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  Typography,
  CardMedia,
  Tooltip,
  useTheme,
  Box,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import {
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setOthersNeeded,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { logEvent } from "services/amplitude.service.js";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
// import { useIsTab } from "hooks/is-tab.hook";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { showNewsguard } from "utils/show-ratings";
// import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import OpenAccessChip from "features/CategoryNews/component/open-access-chip";
import SmileyIcon from "./smiley-icon";
import { useSwiper, useSwiperSlide } from "swiper/react";
import AccessibleIconButton from "components/base/AccessibleIconButton";

const NewsCardComponent = ({ data, query, category, slideIndex }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  // const isTab = useIsTab();
  const dispatch = useDispatch();

  const hashId = useMemo(() => {
    return new Hashes.MD5().hex(data.url);
  }, [data]);

  const openSourceSidebar = (tour = false) => {
    if (!tour) {
      logEvent("click_card_header", {
        host: data.hostName,
        entity: data.entityName,
      });
    }
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const CardHeader = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      onClick={openSourceSidebar}
      sx={{
        borderRadius: 1,
        px: "1rem",
        py: 0.4,
        cursor: "pointer",
        "&:hover": { backgroundColor: "secondary.main" },
      }}
    >
      <Tooltip
        enterDelay={750}
        followCursor
        title={data.entityName}
        placement="bottom-start"
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold",
            width: "20rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "grey.600",
          }}
        >
          {data.entityName}
        </Typography>
      </Tooltip>
      <Stack direction="row" alignItems="center" gap={2}>
        {data.hasOpenAccess &&
          category === "Academia & Scholarly Resources" && <OpenAccessChip />}
        {data.mediaBiasRating && (
          <AllSidesRating
            size="small"
            single
            activeCategory={data.mediaBiasRating}
            className={category === "Global News" ? "tour-all-sides-icon" : ""}
            hash={hashId}
          />
        )}
        {showNewsguard(data.score) && (
          <Box
            className={category === "Global News" ? "tour-newsguard-icon" : ""}
            data-article-id={hashId}
          >
            <NewsguardScore score={Number(data.score)} size="small" />
          </Box>
        )}
        {data.charityNavigatorScore &&
          category !== "Academia & Scholarly Resources" &&
          !data.score &&
          !data.mediaBiasRating && (
            <Box className="tour-charity-icon" data-article-id={hashId}>
              <CharityNavigatorScore
                score={data.charityNavigatorScore}
                size="small"
              />
            </Box>
          )}
      </Stack>
    </Stack>
  );

  const openSidebar = () => {
    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.name,
        published_date: data.datePublishedDisplayText,
        url: data.url,
        utc_date_published: data.datePublished,
      })
    );
    dispatch(setCollectionId(data.collectionId));
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const newUrl = data.url.includes("foreignpolicy.com")
    ? data.url + "?auth=sooth"
    : data.url;

  const BookmarkOptions = () => (
    <Stack
      onClick={() => {
        openSidebar();
        logEvent("open_bookmark_popup", {
          url: newUrl,
          query,
          type: "generic",
          entity: data.entityName,
          category,
          mediabiasrating: data.mediaBiasRating || "NA",
          score: showNewsguard(data.score) ? data.score : "NA",
        });
      }}
      className="card-options"
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      gap={1}
      mt={1}
      sx={{
        cursor: "pointer",
        pl: 1,
        pr: 1,
        borderRadius: 1,
        transition: "all 0.1s ease",
        "&:hover": {
          backgroundColor: theme.palette.secondary.main,
          "& .collection-hover": { opacity: "1 !important" },
        },
      }}
    >
      {data.collectionSourceId ? (
        <CollectionsLogo
          onClick={openSidebar}
          alt="Collections"
          style={{ width: 18, height: 18, fill: "blue", color: "blue" }}
        />
      ) : (
        <CollectionsLogoHover
          className="collection-hover"
          onClick={openSidebar}
          alt="Collections"
          style={{
            width: 17,
            height: 17,
            fill: "blue",
            color: "blue",
            opacity: 0,
            transition: "all 0.1s ease",
          }}
        />
      )}
      <AccessibleIconButton
        title="Open Bookmark"
        sx={{
          p: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <MoreHorizIcon />
      </AccessibleIconButton>
    </Stack>
  );

  const ImageSection = () => {
    const imageUrl = data?.openGraphImage?.contentUrl;
    const [errored, setErrored] = useState(false);

    const onError = ({ currentTarget }) => {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    };

    return (
      <Box
        sx={{
          flexGrow: 1,
          flexBasis: { xs: "30%", sm: "45%", md: "30%", lg: "35%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          height: "100%",
        }}
      >
        <CardMedia
          component="img"
          sx={{
            aspectRatio: 1,
            borderRadius: "10px",
            minHeight: "80px",
            width: "100%",
            objectFit: "cover",
            maxHeight: { xs: "6em", md: "unset" },
          }}
          image={imageUrl || defaultBackground}
          alt={imageUrl ? data.name : "Image not found"}
          onError={onError}
        />
        <BookmarkOptions />
      </Box>
    );
  };

  const swiper = useSwiper();

  useEffect(() => {
    function triggerSlideTo({ detail }) {
      console.log("swiping", slideIndex, swiper);
      swiper.slideTo(slideIndex);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(detail.action, { detail: detail.index })
        );
      }, 0);
    }
    function triggerOpenSourcePanel({ detail }) {
      openSourceSidebar(true);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("tour:app:sourcePanel:onLoad", { detail })
        );
      }, 0);
    }
    window.addEventListener(
      `tour:app:article:slideTo:${hashId}`,
      triggerSlideTo
    );
    window.addEventListener(
      `tour:app:article:sourceOpen:${hashId}`,
      triggerOpenSourcePanel
    );
    return () => {
      window.removeEventListener(
        `tour:app:article:slideTo:${hashId}`,
        triggerSlideTo
      );
      window.removeEventListener(
        `tour:app:article:sourceOpen:${hashId}`,
        triggerOpenSourcePanel
      );
    };
  }, [hashId]);

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        boxShadow: "none",
        borderRadius: 1,
        p: 1,
        transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Stack direction="column" gap={1} justifyContent="space-between">
        <Box sx={{ width: "100%" }}>
          <CardHeader />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              p: "0 1rem",
              mt: 1,
            }}
          >
            <Box sx={{ maxHeight: "5em", mb: 2, minHeight: "3em" }}>
              <SearchResultLink
                url={data.url}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: data.url,
                  query: query?.trim() ? query : undefined,
                  entity: data.entityName,
                  title: data.name,
                  category,
                  mediabiasrating: data.mediaBiasRating || "NA",
                  score: showNewsguard(data.score) ? data.score : "NA",
                  date_published: data.datePublished,
                }}
                variant="body4"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  lineHeight: "1.5em",
                  maxWidth: "90%",
                  flexShrink: 0,
                  cursor: "pointer",
                  "&:hover": { color: theme.palette.primary.light },
                }}
              >
                {data.name}
              </SearchResultLink>
            </Box>
            {data.hasPayWall && data.isPartner ? (
              <SmileyIcon />
            ) : (
              data.hasPayWall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "stretch", px: 1 }}>
          <Stack
            sx={{
              width: "100%",
              flexGrow: 1,
              flexBasis: { xs: "70%", sm: "50%", md: "50%", lg: "65%" },
              pr: 2,
              flexShrink: 0,
            }}
          >
            <Typography
              variant={isMobile ? "body4" : "body1"}
              sx={{
                color: "grey.700",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: { xs: 4, md: 4, lg: 5 },
                WebkitBoxOrient: "vertical",
                maxHeight: { xs: "6em", md: "6em", lg: "7.5em" },
                lineHeight: "1.5em",
                minHeight: "4.5em",
                wordBreak: "break-word",
              }}
            >
              {!isMobile && data.datePublishedDisplayText
                ? `${data.datePublishedDisplayText} - `
                : ""}
              {data.snippet}
            </Typography>
          </Stack>
          <ImageSection />
        </Box>
      </Stack>
    </Card>
  );
};

export const NewsCard = memo(NewsCardComponent);
